import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaBuilding, FaMapMarkerAlt, FaBriefcase, FaUserShield, FaCamera, FaEnvelope, FaToolbox } from 'react-icons/fa';
import { toast } from 'react-toastify';
import TenantService, { BusinessProfile } from '../../settings/company/services/TenantService';
import CompanyProfileTab from '../../settings/company/components/CompanyProfileTab';
import LocationsTab from '../../settings/company/components/LocationsTab';
import WorkAreasTab from './WorkAreasTab';
import PermissionsTab from './PermissionsTab';
import LogoEditorModal from '../../settings/company/components/LogoEditorModal';
import EmailTemplatesPage from '../../settings/email-templates/components/EmailTemplatesPage';
import AssetsTab from './AssetsTab';
import { useUserRole } from '../../../hooks/useUserRole';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';

type OrganizationTabKey = 'company' | 'locations' | 'work-areas' | 'permissions' | 'email-templates' | 'assets';

interface TabConfig {
  key: OrganizationTabKey;
  label: string;
  icon: React.ReactNode;
}

const organizationTabs: TabConfig[] = [
  { key: 'company', label: 'Company', icon: <FaBuilding /> },
  { key: 'locations', label: 'Locations', icon: <FaMapMarkerAlt /> },
  { key: 'work-areas', label: 'Work areas', icon: <FaBriefcase /> },
  { key: 'permissions', label: 'Permissions', icon: <FaUserShield /> },
  { key: 'email-templates', label: 'E-Mail-Vorlagen', icon: <FaEnvelope /> },
  { key: 'assets', label: 'Assets', icon: <FaToolbox /> },
];

const ADMIN_ONLY_ORG_TABS: OrganizationTabKey[] = ['company', 'locations', 'permissions', 'email-templates', 'assets'];

export default function OrganizationPage(): React.JSX.Element {
  const { isAdmin } = useUserRole();
  const visibleTabs = isAdmin ? organizationTabs : organizationTabs.filter(t => !ADMIN_ONLY_ORG_TABS.includes(t.key));
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as OrganizationTabKey | null;
  const initialTab = visibleTabs.find((t) => t.key === tabParam)?.key || visibleTabs[0]?.key || 'work-areas';

  const [activeTab, setActiveTab] = useState<OrganizationTabKey>(initialTab);
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoEditorOpen, setLogoEditorOpen] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      loadProfile();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (tabParam && visibleTabs.find((t) => t.key === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await TenantService.getBusinessProfile();
      setProfile(data);
    } catch (err) {
      console.error('Failed to load business profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabKey: OrganizationTabKey) => {
    setActiveTab(tabKey);
    setSearchParams({ tab: tabKey });
  };

  const handleLogoSave = async (croppedBlob: Blob) => {
    setLogoEditorOpen(false);
    setUploadingLogo(true);

    try {
      const file = new File([croppedBlob], 'logo.png', { type: 'image/png' });
      await TenantService.uploadLogo(file);
      toast.success('Logo uploaded');
      await loadProfile();
    } catch (err: any) {
      console.error('Failed to upload logo:', err);
      const message = err?.response?.data?.detail || 'Upload failed';
      toast.error(message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    setLogoEditorOpen(false);
    setUploadingLogo(true);

    try {
      await TenantService.deleteLogo();
      toast.success('Logo removed');
      await loadProfile();
    } catch (err: any) {
      console.error('Failed to delete logo:', err);
      toast.error('Failed to remove logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const displayName = profile?.trade_name || profile?.legal_name || 'Company';
  const addressParts = [
    profile?.street,
    profile?.street_number,
    profile?.postal_code,
    profile?.city,
  ].filter(Boolean);
  const displayAddress = addressParts.length > 0 ? addressParts.join(' ') : '';

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header Card */}
      <div style={{ ...ps.card, marginBottom: 20, padding: '24px 28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: colors.navy, margin: '0 0 8px 0' }}>
              {loading ? 'Loading...' : displayName}
            </h1>

            {displayAddress && (
              <div style={{ display: 'flex', gap: 24, marginBottom: 8, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaMapMarkerAlt color={colors.textMuted} size={13} />
                  <span style={{ fontSize: 13, color: colors.textMuted }}>{displayAddress}</span>
                </div>
              </div>
            )}

            <div style={{ fontSize: 12, color: colors.textLight }}>
              Organization settings and configuration
            </div>
          </div>

          {/* Logo */}
          <div
            onClick={() => !uploadingLogo && setLogoEditorOpen(true)}
            style={{
              width: 400,
              height: 80,
              borderRadius: 12,
              backgroundImage: profile?.logo_url ? `url(${profile.logo_url})` : 'none',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundColor: 'transparent',
              border: profile?.logo_url ? 'none' : `1px dashed ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: uploadingLogo ? 'not-allowed' : 'pointer',
              position: 'relative',
              transition: 'all 0.2s ease',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              if (!uploadingLogo) {
                e.currentTarget.style.opacity = '0.85';
                e.currentTarget.style.transform = 'scale(1.02)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title={profile?.logo_url ? 'Edit logo' : 'Upload logo'}
          >
            {!profile?.logo_url && !uploadingLogo && (
              <FaBuilding size={24} color={colors.navy} />
            )}
            {uploadingLogo && (
              <div style={{ fontSize: 12, color: colors.textMuted }}>...</div>
            )}
            {!uploadingLogo && (
              <div
                style={{
                  position: 'absolute',
                  bottom: -8,
                  right: -8,
                  background: colors.navy,
                  borderRadius: '50%',
                  width: 22,
                  height: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FaCamera size={9} color="white" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs Card */}
      <div style={{ ...ps.card, padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}`, overflowX: 'auto' }}>
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              style={{
                ...ps.tabButton(activeTab === tab.key),
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '14px 20px',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 24, minHeight: 400 }}>
          {activeTab === 'company' && (
            <CompanyProfileTab
              profile={profile}
              loading={loading}
              onProfileUpdated={loadProfile}
            />
          )}
          {activeTab === 'locations' && <LocationsTab />}
          {activeTab === 'work-areas' && <WorkAreasTab />}
          {activeTab === 'permissions' && <PermissionsTab />}
          {activeTab === 'email-templates' && <EmailTemplatesPage />}
          {activeTab === 'assets' && <AssetsTab />}
        </div>
      </div>

      <LogoEditorModal
        isOpen={logoEditorOpen}
        currentLogoUrl={profile?.logo_url}
        onClose={() => setLogoEditorOpen(false)}
        onSave={handleLogoSave}
        onDelete={handleLogoDelete}
      />
    </div>
  );
}
